/**
 * Interview service - Aligns with backend interview repository and service
 */
import { apiClient, fetchWithCache, createCRUDMethods, authHeaders } from './api';
import { createQueryString } from '../utils/helpers';

class InterviewService {
  constructor() {
    this.baseUrl = '/interviews';
    // Use CRUD methods for basic operations
    this.crud = createCRUDMethods(this.baseUrl);
  }

  // ===============================
  // CRUD Operations
  // ===============================

  async getInterviews(filters = {}, pagination = {}) {
    const params = {
      ...filters,
      skip: pagination.page ? (pagination.page - 1) * pagination.limit : 0,
      limit: pagination.limit || 20,
    };

    return fetchWithCache(`${this.baseUrl}?${createQueryString(params)}`);
  }

  // `options.skipCache: true` bypasses the read-through cache. Needed after a mutation, because
  // apiCache has no invalidation on write — a plain refetch right after pulling a transcript
  // would be served the pre-pull copy from cache and look like the pull did nothing.
  async getInterview(id, options = {}) {
    return fetchWithCache(`${this.baseUrl}/${id}`, options);
  }

  async createInterview(data) {
    const response = await apiClient.post(this.baseUrl, data);
    return response;
  }

  async updateInterview(id, data) {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response;
  }

  async deleteInterview(id) {
    const response = await apiClient.delete(`${this.baseUrl}/${id}`);
    return response;
  }

  // ===============================
  // Statistics & Analytics - UPDATED for new backend
  // ===============================

  async getStats() {
    return fetchWithCache(`${this.baseUrl}/stats/overview`);
  }

  // REMOVED: getDetailedStats - endpoint doesn't exist in new backend
  // async getDetailedStats(dateRange = {}) {
  //   const params = createQueryString(dateRange);
  //   return fetchWithCache(`${this.baseUrl}/stats/detailed?${params}`);
  // }

  // ===============================
  // Transcript & Media Operations - UPDATED for new backend
  // ===============================

  // `options.persist: false` returns the pulled transcript WITHOUT saving it, so the detail view can
  // show it in the editor for review and the user saves explicitly.
  async scrapeTranscript(interviewId, url, options = {}) {
    const persist = options.persist !== false;
    console.log(`🔄 Pulling transcript for interview ${interviewId} from URL:`, url, { persist });

    const response = await apiClient.post(`/interviews/${interviewId}/scrape-transcript`, {
      url: url,
      persist,
    });

    console.log(`✅ Transcript pull completed:`, response.data);
    return response;
  }

  // Pulls one batch of Fireflies transcripts and reports how many still need pulling. Callers loop
  // until `remaining` hits 0 — see PullAllFirefliesButton.
  async bulkPullFirefliesTranscripts(limit = 20) {
    const response = await apiClient.post(`${this.baseUrl}/bulk-pull-transcripts`, { limit });
    return response;
  }

  async updateTranscript(id, transcriptText) {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, {
      transcript_text: transcriptText
    });
    return response;
  }

  async generateSummary(id) {
    const response = await apiClient.post(`${this.baseUrl}/${id}/generate-summary`);
    return response;
  }

  async resetGenerationFlag(interviewId) {
    const response = await apiClient.post(`/processing/interviews/${interviewId}/reset-generation-flag`);
    return response;
  }

  async generateConsumerArticles(interviewId) {
    const response = await apiClient.post(`/processing/interviews/${interviewId}/generate-consumer-articles`);
    return response;
  }

  async generatePerspectiveArticles(interviewId) {
    const response = await apiClient.post(`/processing/interviews/${interviewId}/generate-perspective-article`);
    return response;
  }

  async generateFeatureArticle(interviewId) {
    const response = await apiClient.post(`/processing/interviews/${interviewId}/generate-feature-article`);
    return response;
  }

  // ===============================
  // CSV Import Operations - UPDATED for new backend
  // ===============================

  async analyzeCSV(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post(`${this.baseUrl}/analyze-csv`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response;
  }

  async importCSV(file, updateExisting = false, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('update_existing', updateExisting);
    
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      };
    }
    
    const response = await apiClient.post(`${this.baseUrl}/import-csv-confirmed`, formData, config);
    return response;
  }

  // ===============================
  // Bulk Operations
  // ===============================

  async bulkDelete(interviewIds) {
    const response = await apiClient.post(`${this.baseUrl}/bulk-delete`, {
      interview_ids: interviewIds
    });
    return response;
  }

  async bulkUpdate(interviewIds, updateData) {
    const response = await apiClient.post(`${this.baseUrl}/bulk-update`, {
      interview_ids: interviewIds,
      update_data: updateData
    });
    return response;
  }

  // ===============================
  // Search & Filter Operations
  // ===============================

  async searchInterviews(searchTerm, filters = {}, pagination = {}) {
    const params = {
      search: searchTerm,
      ...filters,
      skip: pagination.page ? (pagination.page - 1) * pagination.limit : 0,
      limit: pagination.limit || 20,
    };

    return fetchWithCache(`${this.baseUrl}/search?${createQueryString(params)}`);
  }

  async getFilterOptions() {
    return fetchWithCache(`${this.baseUrl}/filter-options`);
  }

  // ===============================
  // Validation & Duplicate Check
  // ===============================

  async checkDuplicate(data) {
    const response = await apiClient.post(`${this.baseUrl}/check-duplicate`, data);
    return response;
  }

  async validateInterview(data) {
    const response = await apiClient.post(`${this.baseUrl}/validate`, data);
    return response;
  }

  // ===============================
  // Export Operations
  // ===============================

  async exportToCSV(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.industry) params.append('industry', filters.industry);
      if (filters.search) params.append('search', filters.search);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      // Export configuration
      params.append('include_transcript', 'true');
      params.append('include_ai_fields', 'true');
      
      const response = await fetch(`${this.baseUrl}/export/csv?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'text/csv',
          ...authHeaders(),
        },
      });
      
      if (response.ok) {
        return {
          success: true,
          blob: await response.blob(),
          filename: this.getFilenameFromHeaders(response.headers),
          stats: {
            count: response.headers.get('X-Export-Count'),
            generationTime: response.headers.get('X-Generation-Time')
          }
        };
      } else {
        const errorText = await response.text();
        return {
          success: false,
          error: `Export failed (${response.status}): ${errorText}`
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export CSV'
      };
    }
  }
  
  getFilenameFromHeaders(headers) {
    const contentDisposition = headers.get('Content-Disposition');
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        return filenameMatch[1];
      }
    }
    // Fallback filename
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `interviews_export_${timestamp}.csv`;
  }

  async exportToPDF(id) {
    const response = await apiClient.get(`${this.baseUrl}/${id}/export/pdf`, {
      responseType: 'blob',
    });
    return response;
  }
}

export const interviewService = new InterviewService();
export default interviewService;